import {useEffect, useRef, useState} from 'react'
import {usePersona} from '../../../store/hooks/persona'
import {classes, StyledDivider} from './SummaryComponentStyles'
import {Box, CircularProgress, Typography} from '@mui/material'
import SummaryLinearProgressBar from './SummaryLinearProgressBar'
import SummaryHeader from './SummaryHeader'
import SummaryData from './SummaryData'
import SummaryCommunicationData from './SummaryCommunicationData'
import CustomBox from '../../common/CustomBox'
import PersonaSummary from '../../../api/queries/persona/personaSummaryQuery'
import getRounded from '../../helpers/getRounded'
import ZeroPopulationBanner from '../ZeroPopulationBanner'
import {SuperscriptGreyDagger} from '../../common/Dagger'
import {getPersonaCustomLabel} from '../../Dashboard/helper'
import {useFilterListDetail} from '../../../store/hooks/filterListDetail'
import ChatbotCTA from '../ChatbotCTA/ChatbotCTA'
import {StyledSpaceBox} from '../Styles'

const demonsocioInitial = [
  {title: 'Hispanic or Latino', value: 100},
  {title: 'White', value: 100},
  {title: 'Asian', value: 100},
  {title: 'Black or African American', value: 100},
  {title: 'Multiple Races or Other', value: 100},
  {title: 'American Indian and Alaska Native', value: 100},
  {title: 'Native Hawaiian and Other Pacific Islander', value: 100}
]
const insightsInitial = [
  {text: 'Average Age', title: '--'},
  {text: 'Gender', title: '--'},
  {text: 'Average Income', title: '--'}
]
const engagementLangInitial = [
  {text: '0', title: '--'},
  {text: '0', title: '--'},
  {text: '0', title: '--'}
]
const communicationInitial = [
  {text: '0', title: '--'},
  {text: '0', title: '--'},
  {text: '0', title: '--'}
]

const getPercentageValue = (value) => {
  if (value >= 1) {
    return `${value}%`
  }
  return '<1%'
}

const processDemographics = (demographics) => {
  const tempDemonsocio = structuredClone(demonsocioInitial)

  for (const demo of tempDemonsocio) {
    demo.value = 0
  }

  for (const demo of demographics || []) {
    const idx = tempDemonsocio.findIndex((ele) => ele.title.includes(demo.name))
    tempDemonsocio[idx] = {title: demo.name, value: demo.percentage}
  }

  const sortedDemonsocio = [...tempDemonsocio]
  sortedDemonsocio.sort((a, b) => b.value - a.value)

  return sortedDemonsocio.slice(0, 5)
}

const formatIncomeRange = (incomeRange = '0 - 0') => {
  const range = incomeRange.includes('+') ? `${incomeRange} - 0` : incomeRange

  const parts = range.split(' ')
  parts[0] = parts[0].replaceAll(/[^a-zA-Z0-9]/g, '')
  parts[2] = parts[2].replaceAll(/[^a-zA-Z0-9]/g, '')

  if (parts[0] === 'Less') {
    parts[0] = 0
  }

  return parts
}

const processInsights = (summaryData) => {
  const incomeRange = formatIncomeRange(summaryData?.incomeRange)

  const incomeTitle = incomeRange[2].localeCompare('0')
    ? `$${getRounded(incomeRange[0])} - $${getRounded(incomeRange[2])}`
    : `$${getRounded(incomeRange[0])}+`

  return [
    {text: 'Average Age', title: summaryData?.averageAge},
    {
      text: 'Gender',
      title: `${getPercentageValue(summaryData?.gender?.percentage)} ${summaryData?.gender?.name}`
    },
    {
      text: 'Average Income',
      title: incomeTitle
    }
  ]
}

const processEngagementData = (languages, outreach) => {
  const engagementLang = (languages || []).map((lang) => ({
    title: lang.name,
    text: `${getPercentageValue(lang.percentage)}`
  }))

  const communication = (outreach || []).map((comm) => ({
    title: comm.name,
    text: `${getPercentageValue(comm.percentage)}`
  }))

  return {engagementLang, communication}
}

const SummaryComponent = () => {
  const {personaList} = useFilterListDetail()
  const {personaSidebarOpen, setPersonaSidebarOpen, personas} = usePersona()
  const {personaSummary, loadingPersonaSummary, refetchPersonaSummary, errorLoadingPersonaSummary} = PersonaSummary()
  const summaryRef = useRef(null)
  // State for Summary Data
  const [summary, setSummary] = useState({
    demonsocio: demonsocioInitial.slice(0, 5),
    insights: insightsInitial,
    engagementLang: engagementLangInitial,
    communication: communicationInitial
  })
  const [zeroCountPopulation, setZeroCountPopulation] = useState(false)

  useEffect(() => {
    if (loadingPersonaSummary) {
      return
    }

    const data = personaSummary?.personaSummary

    if (data !== undefined && data !== null) {
      const demonsocio = processDemographics(data.demographics)
      const insights = processInsights(data)
      const {engagementLang, communication} = processEngagementData(data.languages, data.outreach)

      setSummary({
        demonsocio,
        insights,
        engagementLang,
        communication
      })
      setZeroCountPopulation(false)
    } else {
      setSummary({
        demonsocio: [
          {title: '--', value: 0},
          {title: '--', value: 0},
          {title: '--', value: 0},
          {title: '--', value: 0},
          {title: '--', value: 0}
        ],
        insights: insightsInitial,
        engagementLang: engagementLangInitial,
        communication: communicationInitial
      })
      setZeroCountPopulation(true)
    }
  }, [loadingPersonaSummary, personaSummary, errorLoadingPersonaSummary, personas])

  useEffect(() => {
    if (personaSidebarOpen?.open) {
      refetchPersonaSummary()
    }
  }, [personas, personaSidebarOpen?.open, refetchPersonaSummary])

  useEffect(() => {
    if (personaSidebarOpen?.open) {
      summaryRef?.current?.focus({preventScroll: true})
    }
  }, [personaSidebarOpen])

  // This function will handle the custom label for the persona sections
  const handlePersonaCustomLabel = (name = null, title = 'Drivers of Health Personas') => {
    return getPersonaCustomLabel(personaList, title, name, 'summary')
  }

  return (
    <Box ref={summaryRef} tabIndex={-1} aria-label='Persona Summary Section'>
      <SummaryHeader
        setSidebarOpen={setPersonaSidebarOpen}
        sidebarOpen={personaSidebarOpen}
        handlePersonaCustomLabel={handlePersonaCustomLabel}
      />

      <CustomBox className={classes.summaryCustomBox}>
        {loadingPersonaSummary && (
          <CircularProgress
            className={classes.circularProgress}
            size={15}
            title='Loading summary data'
            aria-label='Loading summary data'
            tabIndex={0}
          />
        )}
        {zeroCountPopulation && <ZeroPopulationBanner />}
        <Typography
          className={[classes.summarySubtitle, classes.summaryMargin]}
          aria-label='Demography and SocioEconomic Section'
          tabIndex={0}
        >
          {handlePersonaCustomLabel('DEMOGRAPHY & SOCIOECONOMIC')} {zeroCountPopulation && <SuperscriptGreyDagger />}
        </Typography>
        <SummaryLinearProgressBar data={summary?.demonsocio} />
        <Typography className={classes.summarySubtitle} aria-label='Insights Section' tabIndex={0}>
          {handlePersonaCustomLabel('INSIGHTS')}
        </Typography>
        <SummaryData
          data={summary.insights}
          isZeroCountPopulation={zeroCountPopulation}
          handlePersonaCustomLabel={handlePersonaCustomLabel}
        />
        <StyledDivider aria-hidden='true' />
        <Typography className={classes.summarySubtitle} aria-label='Engagement Section' tabIndex={0}>
          {handlePersonaCustomLabel('ENGAGEMENT')}
        </Typography>
        <Typography className={classes.summarySubtitle2} aria-label='Top 3 Languages' tabIndex={0}>
          {handlePersonaCustomLabel('Top 3 Languages')}
        </Typography>
        <SummaryData
          data={summary.engagementLang}
          isZeroCountPopulation={zeroCountPopulation}
          handlePersonaCustomLabel={handlePersonaCustomLabel}
        />
        <Typography
          className={[classes.summarySubtitle2, classes.summaryTexts]}
          aria-label='Top Communication Preferences'
          tabIndex={0}
        >
          {handlePersonaCustomLabel('Top Communication Preferences')}
        </Typography>
        <SummaryCommunicationData data={summary.communication} isZeroCountPopulation={zeroCountPopulation} />
        <ChatbotCTA />
        <StyledSpaceBox aria-hidden='true' />
      </CustomBox>
    </Box>
  )
}

export default SummaryComponent

/* 
The SummaryComponent is a React component designed to display summarized information, 
likely aggregating or presenting data in a concise format for the user.

 Function Descriptions
  - fetchSummaryData: Retrieves summary data from an API or data source.
  - handleInputChange: Updates state based on user input changes.
  - calculateTotals: Computes total values based on provided data.
  - renderSummaryTable: Renders a table displaying the summary information.
  - exportSummary: Exports the summary data, possibly as a file (e.g., CSV or PDF).
  - resetSummary: Resets the summary data to its initial state.

*/
